import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';

interface Session {
  id: string;
  start_time: string;
  end_time: string | null;
  sedentary_duration_seconds: number;
  avg_posture_score: number | null;
  movement_events: number;
  status: string;
}

interface SessionsResponse {
  sessions: Session[];
  total: number;
}

export function EmployeeSessions() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get<SessionsResponse>('/api/employee/sessions', {
          params: { start_date: startDate, end_date: endDate }
        });
        setSessions(response.data.sessions);
        setError('');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load sessions');
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [startDate, endDate]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: '#10B981', bg: '#D1FAE5', label: 'Active', icon: '🟢' };
      case 'ended':
        return { color: '#6B7280', bg: '#F3F4F6', label: 'Completed', icon: '✓' };
      case 'on_break':
        return { color: '#F59E0B', bg: '#FEF3C7', label: 'On Break', icon: '☕' };
      default:
        return { color: '#6B7280', bg: '#F3F4F6', label: status, icon: '•' };
    }
  };

  const getPostureColor = (score: number | null): string => {
    if (score === null) return '#9CA3AF';
    if (score >= 70) return '#10B981';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const getPostureLabel = (score: number | null): string => {
    if (score === null) return 'No data';
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Needs work';
  };

  // Calculate summary stats
  const totalSedentary = sessions.reduce((acc, s) => acc + s.sedentary_duration_seconds, 0);
  const avgPosture = sessions.filter(s => s.avg_posture_score !== null).length > 0
    ? sessions.reduce((acc, s) => acc + (s.avg_posture_score || 0), 0) / sessions.filter(s => s.avg_posture_score !== null).length
    : null;

  if (loading && sessions.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <p style={styles.loadingText}>Loading your sessions...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Session History</h1>
          <p style={styles.subtitle}>Track your wellness journey over time</p>
        </div>
      </div>

      {/* Date Filter */}
      <div style={styles.filterCard}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
          <div style={styles.filterDivider}>→</div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
        </div>
        
        {/* Summary Stats */}
        <div style={styles.summaryRow}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryIcon}>📅</span>
            <span style={styles.summaryValue}>{sessions.length}</span>
            <span style={styles.summaryLabel}>Sessions</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryIcon}>⏱️</span>
            <span style={styles.summaryValue}>{formatDuration(totalSedentary)}</span>
            <span style={styles.summaryLabel}>Total Time</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryIcon}>🧘</span>
            <span style={{...styles.summaryValue, color: getPostureColor(avgPosture)}}>
              {avgPosture?.toFixed(0) ?? '--'}
            </span>
            <span style={styles.summaryLabel}>Avg Posture</span>
          </div>
        </div>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {sessions.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>📅</span>
          <h3 style={styles.emptyTitle}>No sessions found</h3>
          <p style={styles.emptyText}>Try adjusting your date range</p>
        </div>
      ) : (
        <div style={styles.content}>
          {/* Sessions List */}
          <div style={styles.sessionList}>
            {sessions.map((session, index) => {
              const statusConfig = getStatusConfig(session.status);
              const isSelected = selectedSession?.id === session.id;
              
              return (
                <div
                  key={session.id}
                  style={{
                    ...styles.sessionCard,
                    ...(isSelected ? styles.sessionCardSelected : {}),
                    animationDelay: `${index * 0.05}s`,
                  }}
                  onClick={() => setSelectedSession(session)}
                >
                  <div style={styles.sessionHeader}>
                    <div style={styles.sessionDate}>
                      <span style={styles.sessionDateIcon}>📅</span>
                      <span>{formatDate(session.start_time)}</span>
                    </div>
                    <span style={{
                      ...styles.statusBadge,
                      background: statusConfig.bg,
                      color: statusConfig.color,
                    }}>
                      {statusConfig.icon} {statusConfig.label}
                    </span>
                  </div>
                  
                  <div style={styles.sessionTime}>
                    {formatTime(session.start_time)}
                    {session.end_time && ` — ${formatTime(session.end_time)}`}
                  </div>
                  
                  <div style={styles.sessionMetrics}>
                    <div style={styles.metric}>
                      <span style={styles.metricIcon}>⏱️</span>
                      <span style={styles.metricValue}>{formatDuration(session.sedentary_duration_seconds)}</span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricIcon}>🧘</span>
                      <span style={{...styles.metricValue, color: getPostureColor(session.avg_posture_score)}}>
                        {session.avg_posture_score?.toFixed(0) ?? '--'}
                      </span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricIcon}>💫</span>
                      <span style={styles.metricValue}>{session.movement_events}</span>
                    </div>
                  </div>
                  
                  {/* Progress bar for posture */}
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${session.avg_posture_score ?? 0}%`,
                      background: getPostureColor(session.avg_posture_score),
                    }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          {selectedSession && (
            <div style={styles.detailPanel}>
              <div style={styles.detailHeader}>
                <h3 style={styles.detailTitle}>Session Details</h3>
                <button 
                  style={styles.closeBtn}
                  onClick={() => setSelectedSession(null)}
                >
                  ✕
                </button>
              </div>
              
              <div style={styles.detailContent}>
                <div style={styles.detailSection}>
                  <span style={styles.detailLabel}>Date</span>
                  <span style={styles.detailValue}>{formatDate(selectedSession.start_time)}</span>
                </div>
                
                <div style={styles.detailSection}>
                  <span style={styles.detailLabel}>Time</span>
                  <span style={styles.detailValue}>
                    {formatTime(selectedSession.start_time)}
                    {selectedSession.end_time && ` — ${formatTime(selectedSession.end_time)}`}
                  </span>
                </div>
                
                <div style={styles.detailSection}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={{
                    ...styles.statusBadge,
                    background: getStatusConfig(selectedSession.status).bg,
                    color: getStatusConfig(selectedSession.status).color,
                  }}>
                    {getStatusConfig(selectedSession.status).label}
                  </span>
                </div>
                
                <div style={styles.detailDivider} />
                
                <div style={styles.detailMetricCard}>
                  <span style={styles.detailMetricIcon}>⏱️</span>
                  <div>
                    <span style={styles.detailMetricValue}>
                      {formatDuration(selectedSession.sedentary_duration_seconds)}
                    </span>
                    <span style={styles.detailMetricLabel}>Sedentary Time</span>
                  </div>
                </div>
                
                <div style={styles.detailMetricCard}>
                  <span style={styles.detailMetricIcon}>🧘</span>
                  <div>
                    <span style={{
                      ...styles.detailMetricValue,
                      color: getPostureColor(selectedSession.avg_posture_score)
                    }}>
                      {selectedSession.avg_posture_score?.toFixed(0) ?? '--'}
                    </span>
                    <span style={styles.detailMetricLabel}>
                      Posture Score • {getPostureLabel(selectedSession.avg_posture_score)}
                    </span>
                  </div>
                </div>
                
                <div style={styles.detailMetricCard}>
                  <span style={styles.detailMetricIcon}>💫</span>
                  <div>
                    <span style={styles.detailMetricValue}>{selectedSession.movement_events}</span>
                    <span style={styles.detailMetricLabel}>Movement Events</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '1200px', margin: '0 auto' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '16px',
  },
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #E5E7EB',
    borderTopColor: '#10B981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: { color: '#6B7280', fontSize: '15px' },
  header: { marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#064E3B', margin: 0 },
  subtitle: { fontSize: '15px', color: '#6B7280', marginTop: '6px' },
  filterCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
  },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  filterLabel: { fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' },
  dateInput: {
    padding: '12px 16px',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  filterDivider: { color: '#9CA3AF', fontSize: '18px', marginTop: '20px' },
  summaryRow: {
    display: 'flex',
    gap: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #E5E7EB',
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  summaryIcon: { fontSize: '20px' },
  summaryValue: { fontSize: '20px', fontWeight: 700, color: '#1F2937' },
  summaryLabel: { fontSize: '13px', color: '#6B7280' },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 18px',
    background: '#FEE2E2',
    borderRadius: '12px',
    color: '#DC2626',
    marginBottom: '20px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    textAlign: 'center',
  },
  emptyIcon: { fontSize: '56px', marginBottom: '16px' },
  emptyTitle: { fontSize: '20px', fontWeight: 600, color: '#1F2937', margin: 0 },
  emptyText: { fontSize: '14px', color: '#6B7280', marginTop: '8px' },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 360px',
    gap: '24px',
  },
  sessionList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  sessionCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '2px solid transparent',
    animation: 'fadeIn 0.4s ease-out forwards',
    opacity: 0,
  },
  sessionCardSelected: {
    borderColor: '#10B981',
    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.15)',
  },
  sessionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  sessionDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    color: '#1F2937',
  },
  sessionDateIcon: { fontSize: '16px' },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
  },
  sessionTime: { fontSize: '14px', color: '#6B7280', marginBottom: '12px' },
  sessionMetrics: { display: 'flex', gap: '20px', marginBottom: '12px' },
  metric: { display: 'flex', alignItems: 'center', gap: '6px' },
  metricIcon: { fontSize: '14px' },
  metricValue: { fontSize: '15px', fontWeight: 600, color: '#1F2937' },
  progressBar: {
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.5s ease',
  },
  detailPanel: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    height: 'fit-content',
    position: 'sticky',
    top: '24px',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  detailTitle: { fontSize: '18px', fontWeight: 600, color: '#1F2937', margin: 0 },
  closeBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    background: '#F3F4F6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#6B7280',
  },
  detailContent: { display: 'flex', flexDirection: 'column', gap: '16px' },
  detailSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  detailLabel: { fontSize: '13px', color: '#6B7280' },
  detailValue: { fontSize: '14px', fontWeight: 600, color: '#1F2937' },
  detailDivider: { height: '1px', background: '#E5E7EB', margin: '8px 0' },
  detailMetricCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  detailMetricIcon: { fontSize: '24px' },
  detailMetricValue: { fontSize: '24px', fontWeight: 700, color: '#1F2937', display: 'block' },
  detailMetricLabel: { fontSize: '12px', color: '#6B7280' },
};
